import { DateTime } from 'luxon';

/**
 * Recovery for stale-deploy chunk-load failures.
 *
 * When a new release is deployed, the content-hashed lazy chunks
 * (e.g. `chunk-3EQLNZNX.js`) are renamed and the old files disappear from the
 * server. A tab still running the previous release then fails to fetch a chunk
 * it tries to lazy-load, throwing "Failed to fetch dynamically imported
 * module". The fix is a one-shot reload so the tab picks up the current
 * `index.html` and its new chunk names.
 */

/** Patterns thrown by the various browsers when a lazy chunk can't be loaded. */
const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome/Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /is not a valid JavaScript MIME type/i, // Safari/WebKit — server returned text/html (index.html) for a chunk
  /ChunkLoadError/i, // bundler-emitted name
  /\bNG0750\b/, // Angular @defer, when only the serialized message survives (see below)
];

/**
 * `RuntimeErrorCode.DEFER_LOADING_FAILED` — a `@defer` block whose dependencies
 * failed to load.
 *
 * Angular masks the underlying cause here: `triggerResourceLoading` gathers the
 * dependencies with `Promise.allSettled` and drops the rejected `result.reason`,
 * then throws a fresh `RuntimeError(-750)` carrying no `cause`. With `ngDevMode`
 * off the message is just "NG0750", so the browser patterns above can never
 * match a stale-deploy failure that happens inside a `@defer` block — it would
 * skip the reload and land in Sentry instead. Match the structured code.
 *
 * A defer block can also fail because the chunk was fetched but threw while
 * evaluating, which no reload can fix. The one-shot guard below bounds that to a
 * single wasted reload, after which the error reports normally.
 */
const DEFER_LOADING_FAILED_CODE = -750;

/** True for an Angular `RuntimeError` carrying the given code. */
function hasRuntimeErrorCode(error: unknown, code: number): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === code
  );
}

/**
 * Reload at most once within this window. A genuine stale deploy is fixed by a
 * single reload; if a chunk still fails immediately after reloading, the cause
 * is not a stale deploy and we must stop to avoid an infinite reload loop. A
 * later failure (e.g. another deploy mid-session) reloads again.
 */
const RELOAD_GUARD_MS = 60 * 1000;
const RELOAD_GUARD_KEY = 'chunk_reload_attempt';

/**
 * Extracts a string to match against. `instanceof Error` can be false when the
 * error is serialized, wrapped, or thrown from another execution context (e.g.
 * an iframe), so we also read a `message` property off plain objects before
 * falling back to `String(error)`.
 */
function errorMessage(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

/** True when the error is a failure to fetch/evaluate a lazy-loaded chunk. */
export function isChunkLoadError(error: unknown): boolean {
  if (hasRuntimeErrorCode(error, DEFER_LOADING_FAILED_CODE)) return true;

  const message = errorMessage(error);
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Reloads the page to recover from a stale-deploy chunk-load failure, guarded so
 * it never loops. The failure is expected post-deploy noise, so it is not
 * reported to Sentry. Returns `true` when a reload was triggered.
 */
export function recoverFromChunkLoadError(error: unknown): boolean {
  // Browser-only: no `window`/`sessionStorage` to reload in non-browser
  // contexts (e.g. SSR or unit tests).
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadError(error)) return false;

  const now = DateTime.now().toMillis();

  // The reload guard lives in sessionStorage. If we can't read OR write it
  // (private mode / blocked by policy), we can't tell whether we already
  // reloaded, so we must NOT reload — otherwise a persistently failing chunk
  // would reload the page forever.
  let lastAttempt: number;
  try {
    lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    return false;
  }

  if (now - lastAttempt < RELOAD_GUARD_MS) {
    // Already reloaded very recently and still failing — stop, don't loop.
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}
